import type { Signal } from '@angular/core';

import { AppPath } from '@kernel';

import type { MenuItem } from './nav.types';

export const TAB_TTL_MS = 30 * 60 * 1000;
export const MAX_TABS = 10;

// Routes excluded from in-app tab creation and route caching
export const TAB_EXCLUDED_PATHS: readonly AppPath[] = [
  AppPath.Login,
  AppPath.Unauthorized,
  AppPath.NotFound,
];

export interface MenuArgs {
  readonly counts: {
    readonly inbox: Signal<number>;
    readonly conversations: Signal<number>;
  };
  readonly links: readonly MenuItem[];
}

export const createMenuItems = (args: MenuArgs): readonly MenuItem[] => [
  {
    title: 'side.dashboard',
    path: AppPath.Dashboard,
    icon: 'home',
  },
  {
    title: 'side.quickAccess',
    isSection: true,
  },
  {
    title: 'side.communications.inbox',
    path: AppPath.Inbox,
    icon: 'inbox',
    count: args.counts.inbox,
  },
  {
    title: 'side.communications.chat.conversations',
    path: AppPath.TwixorConversations,
    icon: 'chat',
    count: args.counts.conversations,
  },
  {
    title: 'side.application',
    isSection: true,
  },
  {
    title: 'side.links',
    icon: 'links',
    children: args.links,
  },
  {
    title: 'side.lists',
    icon: 'lists',
    children: [
      { title: 'side.lists.patients', path: AppPath.Patients },
      { title: 'side.lists.facilities', path: AppPath.Facilities },
      { title: 'side.lists.doctors', path: AppPath.Doctors },
      { title: 'side.lists.contacts', path: AppPath.Contacts },
      { title: 'side.lists.drugs', path: AppPath.Drugs },
      { title: 'side.lists.clients', path: AppPath.Clients },
      { title: 'side.lists.affiliations', path: AppPath.Affiliations },
      { title: 'side.lists.pharmacies', path: AppPath.Pharmacies },
      { title: 'side.lists.insurances', path: AppPath.Insurances },
    ],
  },
  {
    title: 'side.tasksActivities',
    icon: 'tasks',
    children: [
      { title: 'side.tasksActivities.tasks', path: AppPath.Tasks },
      { title: 'side.tasksActivities.activities', path: AppPath.Activities },
      { title: 'side.tasksActivities.followUps', path: AppPath.FollowUps },
    ],
  },
  {
    title: 'side.communications',
    icon: 'communication',
    children: [
      { title: 'side.communications.inbox', path: AppPath.Inbox },
      { title: 'side.communications.documentsLibrary', path: AppPath.Documents },
      { title: 'side.communications.receivedDocuments', path: AppPath.ReceivedDocuments },
      {
        title: 'side.communications.chat',
        children: [
          { title: 'side.communications.chat.conversations', path: AppPath.TwixorConversations },
          { title: 'side.communications.chat.analytics', path: AppPath.TwixorAnalytics },
          { title: 'side.communications.chat.monitor', path: AppPath.TwixorMonitor },
          { title: 'side.communications.chat.history', path: AppPath.TwixorHistory },
        ],
      },
    ],
  },
  {
    title: 'side.clinical',
    icon: 'clinical',
    children: [
      { title: 'side.clinical.medReview', path: AppPath.MedReview },
      { title: 'side.clinical.rpmDeviceInventory', path: AppPath.RpmDeviceInventory },
    ],
  },
  {
    title: 'side.money',
    icon: 'money',
    badge: 'common.action.new',
    children: [
      { title: 'side.money.statements', path: AppPath.Statements },
      { title: 'side.money.collections', path: AppPath.Collections },
      { title: 'side.money.payRegister', path: AppPath.PayRegister },
    ],
  },
  {
    title: 'side.insights',
    icon: 'insights',
    children: [
      { title: 'side.insights.managerView', path: AppPath.ManagerView },
      { title: 'side.insights.complianceReporting', path: AppPath.ComplianceReporting },
      { title: 'side.insights.analytics', path: AppPath.Analytics },
    ],
  },
  {
    title: 'side.management',
    isSection: true,
  },
  {
    title: 'side.appConfiguration',
    icon: 'configuration',
    children: [
      { title: 'side.appConfiguration.agencies', path: AppPath.ComplianceAgencies },
      {
        title: 'side.appConfiguration.agencyProgramsStandards',
        path: AppPath.ComplianceProgramsStandards,
      },
      { title: 'side.appConfiguration.taskTemplates', path: AppPath.TaskTemplates },
      { title: 'side.appConfiguration.activityTemplates', path: AppPath.ActivityTemplates },
      { title: 'side.appConfiguration.agreementTemplates', path: AppPath.AgreementTemplates },
      { title: 'side.appConfiguration.forms', path: AppPath.Forms },
      { title: 'side.appConfiguration.documentTemplates', path: AppPath.DocumentTemplates },
      { title: 'side.appConfiguration.formTemplateMapping', path: AppPath.FormsTemplatesMapping },
      {
        title: 'side.appConfiguration.categoriesSharingConfigs',
        path: AppPath.CategoriesSharingConfigs,
      },
      { title: 'side.appConfiguration.synonymManagement', path: AppPath.SynonymManagement },
    ],
  },
  {
    title: 'side.userManagement',
    icon: 'user-management',
    children: [
      { title: 'side.userManagement.users', path: AppPath.Users },
      { title: 'side.userManagement.roles', path: AppPath.Roles },
      { title: 'side.userManagement.teams', path: AppPath.Teams },
      { title: 'side.userManagement.salesTeams', path: AppPath.SalesTeams },
    ],
  },
  {
    title: 'side.integrationTools',
    icon: 'integration',
    children: [
      {
        title: 'side.integrationTools.credentialsManagement',
        path: AppPath.CredentialsManagement,
      },
      { title: 'side.integrationTools.actions', path: AppPath.Actions },
      { title: 'side.integrationTools.webhooks', path: AppPath.Webhooks },
      { title: 'side.integrationTools.kit', path: AppPath.Kit },
    ],
  },
  {
    title: 'side.analyticsManagement',
    icon: 'analytics',
    children: [
      { title: 'side.analyticsManagement.accessManager', path: AppPath.AccessManager },
      {
        title: 'side.analyticsManagement.crystalReportsManager',
        path: AppPath.CrystalReportsManager,
      },
    ],
  },
  {
    title: 'side.systemLists',
    icon: 'system-lists',
    children: [
      { title: 'side.systemLists.programs', path: AppPath.Programs },
      { title: 'side.systemLists.devices', path: AppPath.Devices },
      { title: 'side.systemLists.offices', path: AppPath.Offices },
      { title: 'side.systemLists.tags', path: AppPath.Tags },
      { title: 'side.systemLists.documentsLibrary', path: AppPath.CrmDocuments },
      { title: 'side.systemLists.affiliationTypes', path: AppPath.AffiliationTypes },
      { title: 'side.systemLists.drpReason', path: AppPath.DrpReason },
      { title: 'side.systemLists.followupReason', path: AppPath.FollowupReason },
      { title: 'side.systemLists.blockers', path: AppPath.Blockers },
      { title: 'side.systemLists.links', path: AppPath.Links },
    ],
  },
  {
    title: 'side.logsReports',
    icon: 'logs',
    badge: 'common.action.new',
    children: [
      { title: 'side.logsReports.documentAccessHistory', path: AppPath.DocumentAccessHistory },
      { title: 'side.logsReports.userLoginHistory', path: AppPath.UserLoginHistory },
      { title: 'side.logsReports.alertsNotificationsLog', path: AppPath.AlertsNotificationsLog },
    ],
  },
  {
    title: 'side.aiMe',
    icon: 'aime',
    badge: 'common.action.new',
    children: [],
  },
];
